import { useState, useRef, forwardRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../utils/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar } from 'lucide-react';
import BackButton from '../../components/BackButton';

const CreateClient = () => {
  const navigate = useNavigate();
  const addressInputRef = useRef(null);
  const isAutocompleteInitialized = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    emails: [''],
    phone: '',
    address: '',
    dob: '',
    ndisNumber: '',
    about: '',
    allergies: '',
    medications: '',
    emergencyContact: '',
    planType: 'self',
    goals: '',
    otherInfo: '',
    latitude: '',
    longitude: '',
    placeId: ''
  });

  const [profilePicture, setProfilePicture] = useState(null);
  const [documents, setDocuments] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEmailChange = (index, value) => {
    const updated = [...formData.emails];
    updated[index] = value;
    setFormData(prev => ({ ...prev, emails: updated }));
  };

  const addEmailField = () => {
    setFormData(prev => ({ ...prev, emails: [...prev.emails, ''] }));
  };

  const handleDateInputChange = (e) => {
    const input = e.target.value.replace(/-/g, '/');
    setFormData(prev => ({ ...prev, dob: input }));
  };

  const handleCalendarSelect = (date) => {
    if (!date) return;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const formatted = `${day}/${month}/${year}`;
    setFormData(prev => ({ ...prev, dob: formatted }));
  };

  const convertDisplayDOBtoDate = (dobStr) => {
    const parts = dobStr.split('/');
    if (parts.length !== 3) return null;
    let [day, month, year] = parts;
    if (year.length === 2) {
      year = parseInt(year) < 30 ? '20' + year : '19' + year;
    }
    return new Date(`${year}-${month}-${day}`);
  };

  const handleProfileChange = (e) => {
    setProfilePicture(e.target.files[0]);
  };

  const handleDocumentsChange = (e) => {
    setDocuments(Array.from(e.target.files));
  };

  const initializeGoogleAutocomplete = () => {
    if (!window.google || isAutocompleteInitialized.current) return;
    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['geocode'],
      componentRestrictions: { country: ['au', 'nzl'] }
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) {
        toast.error('Please select a valid address from suggestions.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        placeId: place.place_id
      }));
    });
    isAutocompleteInitialized.current = true;
  };

  const CalendarButton = forwardRef(({ onClick }, ref) => (
    <Calendar
      ref={ref}
      onClick={onClick}
      className="w-6 h-6 text-gray-400 hover:text-gray-200 cursor-pointer mt-3"
    />
  ));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const data = new FormData();

    for (const key in formData) {
      if (key === 'emails') {
        formData.emails.forEach(email => data.append('emails', email));
      } else {
        data.append(key, formData[key]);
      }
    }
    if (profilePicture) data.append('profilePicture', profilePicture);
    documents.forEach(doc => data.append('documents', doc));

    try {
      await axios.post(`${API_BASE_URL}/api/clients`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Client created successfully!');
      setTimeout(() => navigate('/clients'), 1500);
    } catch {
      toast.error('Error creating client');
    }
  };

  return (
    <div className="p-6 bg-[#0E0A1F] min-h-screen text-white">
      <BackButton/>
      <div className="max-w-2xl mx-auto bg-[#1C1235] p-6 rounded-xl shadow">
        <h2 className="text-2xl mb-4 font-bold">Create Client</h2>
        <form onSubmit={handleSubmit} className="space-y-4">

          <div>
            <label className="block mb-1">Full Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required
              placeholder="Full Name" className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
          </div>

          <div>
            <label className="block mb-1">Email Addresses</label>
            {formData.emails.map((email, idx) => (
              <input key={idx} type="email" value={email} required
                onChange={e => handleEmailChange(idx, e.target.value)}
                placeholder="Email address"
                className="w-full mb-2 p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
            ))}
            <button type="button" onClick={addEmailField}
              className="text-xs text-purple-400 underline">
              + Add another email
            </button>
          </div>

          {[{ label: 'Phone Number', name: 'phone' },
            { label: 'NDIS Number', name: 'ndisNumber' },
            { label: 'About the Client', name: 'about' },
            { label: 'Allergies', name: 'allergies' },
            { label: 'Medications', name: 'medications' },
            { label: 'Emergency Contact', name: 'emergencyContact' },
            { label: 'Goals', name: 'goals' },
            { label: 'Other Important Info', name: 'otherInfo' }].map(({ label, name }) => (
            <div key={name}>
              <label className="block mb-1">{label}</label>
              <input type="text" name={name} value={formData[name]} onChange={handleChange}
                placeholder={label} className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
            </div>
          ))}

          <div>
            <label className="block mb-1">Address (Google Maps)</label>
            <input type="text" name="address" value={formData.address}
              onChange={handleChange} ref={addressInputRef} onFocus={initializeGoogleAutocomplete} required
              placeholder="Start typing address..." className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
          </div>

          <div>
            <label className="block mb-1">Date of Birth (DD/MM/YY)</label>
            <div className="flex items-center gap-2">
              <input type="text" name="dob" value={formData.dob} onChange={handleDateInputChange}
                placeholder="dd/MM/yyyy" className="flex-1 p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
              <DatePicker selected={formData.dob ? convertDisplayDOBtoDate(formData.dob) : null}
                onChange={handleCalendarSelect} dateFormat="dd/MM/yy"
                customInput={<CalendarButton />} showMonthDropdown showYearDropdown dropdownMode="select" />
            </div>
          </div>

          <div>
            <label className="block mb-1">Plan Type</label>
            <select name="planType" value={formData.planType} onChange={handleChange}
              className="w-full p-3 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]">
              <option value="self">Self-managed</option>
              <option value="plan">Plan-managed</option>
            </select>
          </div>

          <div>
            <label className="block mb-1">Profile Picture</label>
            <input type="file" name="profilePicture" accept="image/*"
              onChange={handleProfileChange}
              className="w-full p-2 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
          </div>

          <div>
            <label className="block mb-1">Supporting Documents (.pdf, .doc)</label>
            <input type="file" name="documents" multiple accept=".pdf,.doc,.docx"
              onChange={handleDocumentsChange}
              className="w-full p-2 rounded bg-[#2A1C4A] text-white border border-[#3B2D5F]" />
          </div>

          <button type="submit"
            className="w-full py-3 bg-[#8F4EFF] hover:bg-purple-700 rounded font-semibold">
            Save Client
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateClient;
